// @flow
import isObject from "lodash/isObject";
import * as React from "react";
import { hasText } from "utils/helpers";
import {
  dateOfBirthRequiredSchema,
  emailSchema,
  firstNameSchema,
  lastNameSchema,
  schemasValidate,
} from "utils/schemas";
import { ZentakeContext } from "utils/ZentakeContext";

type Patient = {
  id?: string,
  first_name?: string,
  last_name?: string,
  date_birth?: string,
  email?: string,
};

type UseTabletUserDataReturn = {
  formError: {
    first_name: boolean,
    last_name: boolean,
    date_birth: boolean,
    email: boolean,
  },
  formErrorLabel: {
    first_name: ?string,
    last_name: ?string,
    date_birth: ?string,
    email: ?string,
  },
  hasFormError: boolean,
  isNewPatient: boolean,
  onClearSelectedPerson: () => void,
  onPersonSelected: (Patient) => void,
  patient: ?Patient,
  selectedPatientHasDateOfBirth: boolean,
  setDateOfBirth: (value: string) => void,
  setFirstName: (value: string) => void,
  setLastName: (value: string) => void,
  validateDateOfBirth: () => void,
  validateFirstName: () => void,
  validateForm: () => Promise<void>,
  validateLastName: () => void,
  setEmail: (email: string) => void,
  validateEmail: () => Promise<void>,
};

const initialFormError = {
  first_name: false,
  last_name: false,
  date_birth: false,
  email: false,
};

const initialFormErrorLabel = {
  first_name: undefined,
  last_name: undefined,
  date_birth: undefined,
  email: undefined,
};

function useTabletUserData(): UseTabletUserDataReturn {
  const { teamConfig } = React.useContext(ZentakeContext);

  const [isNewPatient, setIsNewPatient] = React.useState(true);
  const [patient, setPatient] = React.useState(null);
  const [selectedPatientHasDateOfBirth, setPatientHasDateOfBirth] =
    React.useState(false);

  const [formError, setFormError] = React.useState(initialFormError);
  const [formErrorLabel, setFormErrorLabel] = React.useState(
    initialFormErrorLabel,
  );
  const hasFormError =
    isObject(formError) &&
    Object.values(formError)?.some((err) => err === true);

  function onPersonSelected(patient: Patient) {
    if (!patient?.first_name && !patient?.last_name && !patient?.date_birth) {
      return;
    }
    setPatient(patient);
    setIsNewPatient(false);
    setPatientHasDateOfBirth(hasText(patient?.date_birth));
    schemasValidate(
      [
        ["first_name", patient?.first_name, firstNameSchema],
        ["last_name", patient?.last_name, lastNameSchema],
        ...(hasText(patient?.date_birth)
          ? [["date_birth", patient?.date_birth, dateOfBirthRequiredSchema]]
          : []),
      ],
      setFormError,
      setFormErrorLabel,
    );
  }

  function setFirstName(first_name: string) {
    setPatient((patient) => ({ ...patient, first_name }));
    schemasValidate(
      [["first_name", first_name, firstNameSchema]],
      setFormError,
      setFormErrorLabel,
    );
  }

  function setEmail(email: string) {
    setPatient((patient) => ({ ...patient, email }));
    schemasValidate(
      [["email", email, emailSchema]],
      setFormError,
      setFormErrorLabel,
    );
  }

  function setLastName(last_name: string) {
    setPatient((p) => ({ ...p, last_name }));
    schemasValidate(
      [["last_name", last_name, lastNameSchema]],
      setFormError,
      setFormErrorLabel,
    );
  }
  function setDateOfBirth(date_birth: ?string) {
    if (!date_birth) return;

    const dateInput = new Date(date_birth);
    const isValidDate = dateInput instanceof Date && !isNaN(dateInput);

    setPatient((patient) => ({
      ...patient,
      date_birth: isValidDate ? date_birth : undefined,
    }));

    schemasValidate(
      [["date_birth", date_birth, dateOfBirthRequiredSchema]],
      setFormError,
      setFormErrorLabel,
    );
  }

  function validateDateOfBirth() {
    if (!patient?.date_birth) return;
    return schemasValidate(
      [["date_birth", patient?.date_birth, dateOfBirthRequiredSchema]],
      setFormError,
      setFormErrorLabel,
    );
  }

  async function validateForm() {
    const schemas = [
      ["first_name", patient?.first_name, firstNameSchema],
      ["last_name", patient?.last_name, lastNameSchema],
    ];

    if (teamConfig?.ask_dob) {
      schemas.push([
        "date_birth",
        patient?.date_birth,
        dateOfBirthRequiredSchema,
      ]);
    }

    if (teamConfig?.ask_email) {
      schemas.push(["email", patient?.email, emailSchema]);
    }

    return schemasValidate(schemas, setFormError, setFormErrorLabel);
  }

  const validateFirstName = React.useCallback(() => {
    return schemasValidate(
      [["first_name", patient?.first_name, firstNameSchema]],
      setFormError,
      setFormErrorLabel,
    );
  }, [patient?.first_name]);

  const validateEmail = React.useCallback(() => {
    return schemasValidate(
      [["email", patient?.email, emailSchema]],
      setFormError,
      setFormErrorLabel,
    );
  }, [patient?.email]);

  const validateLastName = React.useCallback(() => {
    return schemasValidate(
      [["last_name", patient?.last_name, lastNameSchema]],
      setFormError,
      setFormErrorLabel,
    );
  }, [patient?.last_name]);

  function onClearSelectedPerson() {
    setPatient(null);
    setIsNewPatient(true);
    setPatientHasDateOfBirth(false);
  }

  return {
    formError,
    formErrorLabel,
    hasFormError,
    isNewPatient,
    onClearSelectedPerson,
    onPersonSelected,
    patient,
    selectedPatientHasDateOfBirth,
    setDateOfBirth,
    setFirstName,
    setEmail,
    setLastName,
    validateDateOfBirth,
    validateFirstName,
    validateForm,
    validateLastName,
    validateEmail,
  };
}

export default useTabletUserData;
